import { formatDate } from '@luxuryescapes/lib-refunds'
import { useIntl } from 'react-intl'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Typography from 'LuxKit/Typography/Typography'
import InlineIcon from 'components/utils/InlineIcon'
import type { ReactElement } from 'react'
import * as React from 'react'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'

interface Props {
  icon?: ReactElement | false
  cancellationPolicy: App.BedbankCancellationPolicy
  showHotelOnly?: boolean
}

function BedbankPropertyCancellationPoliciesBase({ icon, cancellationPolicy, showHotelOnly = true }: Props) {
  const intl = useIntl()
  const hasRateCancellationPolicy = !!cancellationPolicy.policies.length

  const formattedDate = hasRateCancellationPolicy
    ? formatDate.formattedShortDateByTimezone(cancellationPolicy.policies[0]?.start, cancellationPolicy.policies[0]?.timezone)
    : undefined

  const highlightValue = (chunks: React.ReactNode) => <Typography colour="highlight-secondary" as="b" wrap="no-wrap">{chunks}</Typography>

  const cancellationLabel = (() => {
    if (hasRateCancellationPolicy && formattedDate && showHotelOnly) {
      return intl.formatMessage({
        id: '_UOXpRkJSI3QsryTl3kK',
        defaultMessage: '<highlight>Free cancellation</highlight> until {date} (hotel only)',
        description: 'Free cancellation with deadline date and hotel only qualifier',
      }, { date: formattedDate, highlight: highlightValue })
    }
    if (hasRateCancellationPolicy && formattedDate) {
      return intl.formatMessage({
        id: 'TqR6xjNhrVT73n9iws_1',
        defaultMessage: '<highlight>Free cancellation</highlight> until {date}',
        description: 'Free cancellation with deadline date for bedbank property cancellation policy',
      }, { date: formattedDate, highlight: highlightValue })
    }
    if (showHotelOnly) {
      return intl.formatMessage({
        id: 'JWw7UgoKoUb168_PHEms',
        defaultMessage: '<highlight>Free cancellation</highlight> (hotel only)',
        description: 'Free cancellation with hotel only qualifier in the cancellation flow',
      }, { highlight: highlightValue })
    }
    return intl.formatMessage({
      id: '9wiJtoEELLbKy5maMYCD',
      defaultMessage: '<highlight>Free cancellation</highlight>',
      description: 'Free cancellation text for bedbank property cancellation policy',
    }, { highlight: highlightValue })
  })()

  return <BodyTextBlock startIcon={icon} variant="medium">
    {cancellationLabel}
    <Typography as="span" wrap="no-wrap">
      <InlineIcon Icon={LineInfoCircleIcon} />
    </Typography>
  </BodyTextBlock>
}

export default BedbankPropertyCancellationPoliciesBase
